import React, { createContext, useContext, useReducer, useRef } from "react"
import { getCopy, getProps } from "@/lib/component"
import { maybeValidUser } from "@/lib/helpers/user"

export const AppContext = createContext()

const createInitialState = ({
  context,
  pathData,
  mainElem,
  mainCTA,
  searchFormRef
}) => {
  const object = getProps(pathData.props, "object")
  const copy = getCopy(pathData.copy)
  const meta = pathData.meta
  const adminBar = maybeValidUser()

  let crumbs = [{ label: "Startsida", uri: "/" }]

  if (object.parents && object.parents.length > 0) {
    object.parents.forEach((parent) => {
      crumbs.push({ label: parent.title, uri: parent.permalink })
    })
  }

  crumbs.push({ label: object.title, uri: null })

  return {
    mainElem,
    mainCTA,
    searchFormRef,
    context,
    pathData,
    object: object,
    meta: meta,
    copy: copy,
    breadcrumbs: crumbs,
    adminBar: adminBar
  }
}

export const AppProvider = ({ pathData, context, children }) => {
  const mainCTA = useRef()
  const mainElem = useRef()
  const checkoutFormRef = useRef()
  const searchFormRef = useRef()

  const [state, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case "SET_PATH_DATA": {
          return createInitialState({
            ...state,
            pathData: action.pathData,
            context: action.context
          })
        }

        case "RESET": {
          return createInitialState(action.payload)
        }

        default:
          return state
      }
    },
    { context, pathData, mainElem, mainCTA, checkoutFormRef, searchFormRef },
    createInitialState
  )

  const setPathData = (pathData) =>
    dispatch({ type: "SET_PATH_DATA", pathData })

  const resetAppContext = () => {
    dispatch({
      type: "RESET",
      payload: {
        context,
        pathData,
        mainElem,
        mainCTA,
        checkoutFormRef,
        searchFormRef
      }
    })
  }

  return (
    <AppContext.Provider
      value={{
        context: state.context,
        pathData: state.pathData,
        mainElem: state.mainElem,
        mainCTA: state.mainCTA,
        searchFormRef: state.searchFormRef,
        object: state.object,
        copy: state.copy,
        meta: state.meta,
        breadcrumbs: state.breadcrumbs,
        adminBar: state.adminBar,
        resetAppContext,
        setPathData
      }}>
      {children}
    </AppContext.Provider>
  )
}

export const useAppContext = () => {
  return useContext(AppContext)
}

export const usePathData = () => {
  const { pathData } = useAppContext()

  return pathData
}

export const useTheme = () => {
  const pathData = usePathData()

  return {
    isGiftPage: pathData.template === "gift"
  }
}

export const usePathDataContent = () => {
  const { content } = usePathData()

  return content
}

export const useMeta = () => {
  const { meta } = usePathData()

  return meta
}

export const useMenus = () => {
  const { menus } = usePathData()

  return menus
}

export const usePropsObject = () => {
  const { object } = useAppContext()

  return object
}

export const useCopy = () => {
  const { copy } = useAppContext()

  return copy
}

export const useBreadcrumbs = () => {
  const { breadcrumbs } = useAppContext()

  return breadcrumbs
}

export const useAdminBar = () => {
  const { adminBar } = useAppContext()
  return adminBar
}

export const useSiteContext = () => {
  const { context } = useAppContext()
  return context
}

export default AppContext
